import React, { useId } from "react";
import {
  Modal,
  View,
  StyleSheet,
  ScrollView,
  useWindowDimensions,
  type ModalProps,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { useFocusTrap } from "../../../../hooks/useFocusTrap";
import CustomButton from "../CustomButton/CustomButton";
import CustomText from "../CustomText/CustomText";
import { ModalExit } from "../../../svgIcons";
import { shadowStyles } from "../../../../styles/shadow";
import { PaperProvider } from "react-native-paper";
import { getModalAnimationType } from "../../../../utils/getModalAnimationType";

interface CustomModalProps extends Omit<ModalProps, "visible" | "onRequestClose"> {
  visible: boolean;
  onRequestClose: () => void;
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  containerStyle?: StyleProp<ViewStyle>;
  bodyStyle?: StyleProp<ViewStyle>;
  showCloseButton?: boolean;
  title?: string;
  exitFill?: string;
  onShow?: ModalProps["onShow"];
  ariaLabel?: string;
  translateTitle?: boolean;
}

const CustomModal: React.FC<CustomModalProps> = ({
  visible,
  onRequestClose,
  children,
  style = {},
  containerStyle = {},
  bodyStyle = {},
  showCloseButton = false,
  title = "",
  exitFill = "#000000",
  onShow = undefined,
  ariaLabel = "",
  translateTitle = true,
  animationType,
  ...props
}) => {
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();

  // Keep the modal usable on small screens:
  // - Always leave gutters so it never touches screen edges
  // - Constrain height so header stays reachable
  const horizontalGutter = 16;
  const verticalGutter = 24;
  const maxModalWidth = Math.max(0, windowWidth - horizontalGutter * 2);
  const maxModalHeight = Math.max(0, windowHeight - verticalGutter * 2);

  // Extract custom maxWidth from style prop if provided
  const flatStyle = StyleSheet.flatten(style);
  const customMaxWidth = flatStyle?.maxWidth;
  let effectiveMaxWidth: number | string = Math.min(900, maxModalWidth);

  if (customMaxWidth !== undefined) {
    if (typeof customMaxWidth === "string") {
      // Keep string values as-is to preserve units (rem, px, etc.)
      effectiveMaxWidth = customMaxWidth;
    } else if (typeof customMaxWidth === "number") {
      effectiveMaxWidth = Math.min(customMaxWidth, maxModalWidth);
    }
  }

  const uniqueId = useId();
  const titleId = `modal-title-${uniqueId}`;
  const resolvedAriaLabel = ariaLabel || title;

  const { trapRef: modalContentRef } = useFocusTrap({
    visible,
    onEscape: onRequestClose,
  });

  return (
    <Modal
      role="dialog"
      aria-modal={true}
      aria-labelledby={title ? titleId : undefined}
      aria-label={!title && resolvedAriaLabel ? resolvedAriaLabel : undefined}
      testID="activity-modal"
      animationType={getModalAnimationType(animationType ?? "fade")}
      transparent={true}
      visible={visible}
      onRequestClose={onRequestClose}
      onShow={onShow}
      style={{ overflow: "hidden", height: "100%" }}
      {...props}>
      <PaperProvider>
        <View style={styles.centeredView}>
          <View
            ref={modalContentRef}
            style={[
              styles.modalView,
              shadowStyles.modal,
              style,
              // Enforce viewport constraints last so callers can't override them.
              // @ts-expect-error - string maxWidth (e.g., "36rem") works on web
              {
                maxWidth: effectiveMaxWidth,
                maxHeight: maxModalHeight,
              },
              containerStyle,
            ]}>
            {(title || showCloseButton) && (
              <View style={styles.header}>
                <CustomText
                  nativeID={titleId}
                  role="heading"
                  accessibilityRole="header"
                  aria-level={1}
                  text={title}
                  size="s"
                  textType="display"
                  weight="ultraBold"
                  useTranslationText={false}
                  style={undefined}
                />
                {showCloseButton && (
                  <CustomButton
                    styleType="transparent"
                    leftIcon={<ModalExit fill={exitFill} />}
                    onPress={onRequestClose}
                    aria-label="Close modal"
                    accessibilityLabel="Close modal"
                  />
                )}
              </View>
            )}
            <ScrollView
              style={[styles.body, bodyStyle]}
              keyboardShouldPersistTaps="handled"
              contentContainerStyle={{ flexGrow: 1, minHeight: 0, overflow: "hidden", height: "100%" }}
              showsVerticalScrollIndicator={false}
              tabIndex={0}>
              {children}
            </ScrollView>
          </View>
        </View>
      </PaperProvider>
    </Modal>
  );
};

const styles = StyleSheet.create({
  centeredView: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    paddingHorizontal: 16,
    paddingVertical: 24,
  },
  modalView: {
    borderRadius: 20,
    padding: 20,
    width: "100%",
    overflow: "visible",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 5,
  },
  body: {
    flexShrink: 1,
    flexGrow: 1,
  },
});

export default CustomModal;
